/**
 * DisclaimerModal Component
 *
 * A modal dialog shown on first load that requires the user to acknowledge
 * a disclaimer before using the application.
 *
 * The acknowledgment is stored in localStorage so users don't see it
 * every time they visit.
 */

import { useState, useRef, useCallback } from 'react';
import { AlertTriangleIcon } from './icons';
import { useModalChrome } from '../hooks/useModalChrome';

const DISCLAIMER_ACCEPTED_KEY = 'hipponics-firewatch-disclaimer-accepted';
const DISCLAIMER_VERSION = '1.0'; // Increment to force re-acknowledgment

interface DisclaimerModalProps {
  onAccept: () => void;
}

export default function DisclaimerModal({ onAccept }: DisclaimerModalProps) {
  const [isChecked, setIsChecked] = useState(false);

  // Lock body scroll + trap focus while this first-run gate is up (background
  // map previously scrolled behind it on touch). Escape is deliberately inert
  // (no-op onClose) — acknowledgement is mandatory, so the modal can only be
  // dismissed via the checkbox + Continue button.
  const modalRef = useRef<HTMLDivElement>(null);
  const noop = useCallback(() => {}, []);
  useModalChrome({ isOpen: true, onClose: noop, modalRef });

  const handleAccept = () => {
    // Store acceptance with version
    localStorage.setItem(
      DISCLAIMER_ACCEPTED_KEY,
      JSON.stringify({
        version: DISCLAIMER_VERSION,
        acceptedAt: new Date().toISOString(),
      })
    );
    onAccept();
  };

  return (
    <div className="disclaimer-overlay" role="dialog" aria-modal="true" aria-labelledby="disclaimer-title">
      <div className="disclaimer-modal" ref={modalRef}>
        <div className="disclaimer-header">
          <span className="disclaimer-icon" aria-hidden="true">
            <AlertTriangleIcon size={28} />
          </span>
          <h2 id="disclaimer-title">Important Safety Information</h2>
        </div>

        <div className="disclaimer-content">
          <div className="disclaimer-section">
            <h3>This is NOT an Official Emergency Service</h3>
            <p>
              Hipponics Firewatch is an independent tool that aggregates publicly
              available information about bushfires and related events. It is{' '}
              <strong>not affiliated with</strong> any government agency or
              emergency service.
            </p>
          </div>

          <div className="disclaimer-section">
            <h3>Always Follow Official Instructions</h3>
            <p>
              In an emergency, you must <strong>always follow instructions</strong>{' '}
              from official emergency services:
            </p>
            <ul>
              <li>NSW Rural Fire Service (RFS) &amp; Fire and Rescue NSW</li>
              <li>VIC Country Fire Authority (CFA) &amp; VicEmergency</li>
              <li>QLD Fire and Emergency Services (QFES)</li>
              <li>SA Country Fire Service (CFS)</li>
              <li>WA Department of Fire and Emergency Services (DFES)</li>
              <li>TAS Fire Service (TFS)</li>
              <li>ACT Emergency Services Agency (ESA)</li>
              <li>State Emergency Service (SES) and Police in your jurisdiction</li>
              <li>Bureau of Meteorology (BOM) for weather and fire-danger warnings</li>
              <li>Your local council and emergency management authority</li>
            </ul>
          </div>

          <div className="disclaimer-section">
            <h3>Information May Be Delayed or Inaccurate</h3>
            <p>
              The information displayed in this tool:
            </p>
            <ul>
              <li>May be delayed by several minutes</li>
              <li>May not reflect the current situation</li>
              <li>Should never be used as the sole basis for safety decisions</li>
              <li>Is provided "as is" without any guarantee of accuracy</li>
            </ul>
          </div>

          <div className="disclaimer-section disclaimer-emergency">
            <h3>If You Are in Immediate Danger</h3>
            <p>
              <strong>Call Triple Zero (000) immediately.</strong>
            </p>
            <p>
              Do not rely on this application in life-threatening situations.
              Leave early if you feel unsafe. Follow your Bushfire Survival Plan.
            </p>
          </div>
        </div>

        <div className="disclaimer-footer">
          <label className="disclaimer-checkbox">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
            />
            <span>
              I understand this is not an official emergency service and I will
              always follow instructions from NSW RFS and other emergency services.
            </span>
          </label>

          <button
            className="disclaimer-accept-button"
            onClick={handleAccept}
            disabled={!isChecked}
          >
            I Understand - Continue to Map
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Check if the user has already accepted the disclaimer.
 */
export function hasAcceptedDisclaimer(): boolean {
  try {
    const stored = localStorage.getItem(DISCLAIMER_ACCEPTED_KEY);
    if (!stored) return false;

    const data = JSON.parse(stored);
    // Check if the stored version matches current version
    return data.version === DISCLAIMER_VERSION;
  } catch {
    return false;
  }
}
